import "./style.css";
import { HeadCoupledScene } from "./head-coupled-scene";
import { RobotPlayer } from "./player";
import type { ActionEvent, ActionEventName, Landmark, WorkerRequest, WorkerResponse } from "./types";
import {
  currentOrientation,
  NOMINAL_EYE_Z,
  ViewpointController,
  type EyePosition,
  type ViewCalibration,
} from "./viewpoint";

const app = document.querySelector<HTMLElement>("#app")!;
const sceneCanvas = document.querySelector<HTMLCanvasElement>("#scene-canvas")!;
const robotVideo = document.querySelector<HTMLVideoElement>("#robot-video")!;
const cameraVideo = document.querySelector<HTMLVideoElement>("#camera-video")!;
const cameraState = document.querySelector<HTMLElement>("#camera-state")!;
const startPanel = document.querySelector<HTMLElement>("#start-panel")!;
const startButton = document.querySelector<HTMLButtonElement>("#start-button")!;
const startCopy = document.querySelector<HTMLElement>("#start-copy")!;
const startError = document.querySelector<HTMLElement>("#start-error")!;
const progress = document.querySelector<HTMLElement>("#load-progress")!;
const progressBar = document.querySelector<HTMLElement>("#load-bar")!;
const actionLabel = document.querySelector<HTMLElement>("#action-label")!;
const statusDetail = document.querySelector<HTMLElement>("#status-detail")!;
const debugButton = document.querySelector<HTMLButtonElement>("#debug-button")!;
const debugPanel = document.querySelector<HTMLElement>("#debug-panel")!;
const debugCanvas = document.querySelector<HTMLCanvasElement>("#debug-canvas")!;
const debugPose = document.querySelector<HTMLElement>("#debug-pose")!;
const debugHand = document.querySelector<HTMLElement>("#debug-hand")!;
const debugLatency = document.querySelector<HTMLElement>("#debug-latency")!;
const debugViewpoint = document.querySelector<HTMLElement>("#debug-viewpoint")!;
const debugRender = document.querySelector<HTMLElement>("#debug-render")!;
const debugEvent = document.querySelector<HTMLElement>("#debug-event")!;
const debug3dToggle = document.querySelector<HTMLButtonElement>("#debug-3d-toggle")!;
const debugRecalibrate = document.querySelector<HTMLButtonElement>("#debug-recalibrate")!;
const guideButton = document.querySelector<HTMLButtonElement>("#guide-button")!;
const guidePanel = document.querySelector<HTMLElement>("#guide-panel")!;
const guideClose = document.querySelector<HTMLButtonElement>("#guide-close")!;
const calibrationPanel = document.querySelector<HTMLElement>("#calibration-panel")!;
const calibrationTitle = document.querySelector<HTMLElement>("#calibration-title")!;
const calibrationCopy = document.querySelector<HTMLElement>("#calibration-copy")!;
const calibrationButton = document.querySelector<HTMLButtonElement>("#calibration-button")!;

const player = new RobotPlayer(robotVideo);
const worker = new Worker(new URL("./inference.worker.ts", import.meta.url), { type: "module" });
const CALIBRATION_KEY = "interactive-billboard-3d-calibration";

function loadCalibration(): ViewCalibration | null {
  try {
    const value = sessionStorage.getItem(CALIBRATION_KEY);
    if (!value) return null;
    const calibration = JSON.parse(value) as ViewCalibration;
    if (
      calibration.orientation !== currentOrientation()
      || ![calibration.centerX, calibration.centerY, calibration.centerZ].every(Number.isFinite)
    ) return null;
    return calibration;
  } catch {
    return null;
  }
}

const viewpointController = new ViewpointController(loadCalibration());
let lastEyePosition: EyePosition = { x: 0, y: 0, z: NOMINAL_EYE_Z };
let sceneEnabled = true;
const scene3d = new HeadCoupledScene(sceneCanvas, {
  onFrame: (now, deltaMs, aspect) => {
    lastEyePosition = viewpointController.update(now, deltaMs, aspect);
    return lastEyePosition;
  },
  onFallback: (reason) => {
    app.classList.remove("scene-active");
    app.classList.add("scene-fallback");
    debugRender.textContent = `3D：已回退（${reason}）`;
    player.setActive(true);
    void player.unlock();
  },
});
if (scene3d.initialize()) {
  app.classList.add("scene-active");
  player.setActive(false);
}
let stream: MediaStream | null = null;
let workerReady = false;
let cameraReady = false;
let framePending = false;
let running = false;
let posePresent = false;
let burstUntil = 0;
let lastFrameSentAt = 0;
let debugEnabled = false;
let eventTimer = 0;
let viewpointVisibleSince = 0;

const EVENT_LABELS: Record<ActionEventName, string> = {
  USER_ENTERED: "嗨！来和我互动一下吧",
  USER_WAVED: "收到你的挥手啦",
  USER_LEFT_HAND: "你举起了左手",
  USER_RIGHT_HAND: "你举起了右手",
  USER_BOTH_HANDS: "双手举起来，一起庆祝！",
  USER_ARMS_OPEN: "这个拥抱很有感染力",
  USER_LEFT: "再见，期待下次见",
};

const EVENT_PRIORITY: Record<ActionEventName, number> = {
  USER_ENTERED: 40,
  USER_LEFT_HAND: 55,
  USER_RIGHT_HAND: 55,
  USER_ARMS_OPEN: 72,
  USER_WAVED: 80,
  USER_BOTH_HANDS: 85,
  USER_LEFT: 90,
};

function post(message: WorkerRequest, transfer?: Transferable[]): void {
  worker.postMessage(message, transfer ?? []);
}

async function startCamera(): Promise<void> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("当前浏览器不支持摄像头，请使用 iOS 17 以上的 Safari。");
  }
  stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: "user",
      width: { ideal: 640 },
      height: { ideal: 480 },
      frameRate: { ideal: 30, max: 30 },
    },
  });
  cameraVideo.srcObject = stream;
  await cameraVideo.play();
  cameraReady = true;
  cameraState.textContent = "正在识别";
  cameraState.classList.add("active");
  maybeRun();
}

function maybeRun(): void {
  if (!workerReady || !cameraReady || running) return;
  running = true;
  startPanel.classList.add("hidden");
  actionLabel.textContent = "站到镜头前试试看";
  statusDetail.textContent = "机器人会根据你的动作做出回应";
  scheduleFrame();
}

function scheduleFrame(): void {
  if (!running) return;
  const requestFrame = cameraVideo.requestVideoFrameCallback?.bind(cameraVideo);
  if (requestFrame) {
    requestFrame(() => void sendFrame());
  } else {
    window.requestAnimationFrame(() => void sendFrame());
  }
}

async function sendFrame(): Promise<void> {
  if (!running || document.hidden) {
    scheduleFrame();
    return;
  }
  const now = performance.now();
  const interval = now < burstUntil ? 40 : posePresent ? 50 : 100;
  if (!framePending && now - lastFrameSentAt >= interval && cameraVideo.readyState >= 2) {
    try {
      const frame = await createImageBitmap(cameraVideo);
      framePending = true;
      lastFrameSentAt = now;
      post({ type: "FRAME", frame, timestamp: now }, [frame]);
    } catch {
      // A frame may become unavailable while Safari changes page visibility.
    }
  }
  scheduleFrame();
}

function showEvent(event: ActionEvent): void {
  player.trigger(event.name);
  scene3d.trigger(event.name);
  actionLabel.textContent = EVENT_LABELS[event.name];
  statusDetail.textContent = event.name === "USER_ENTERED"
    ? "挥挥手，我会立即回应你"
    : event.name === "USER_WAVED"
      ? "已识别手腕往返或手掌摆动"
      : "动作已识别";
  debugEvent.textContent = `事件：${event.name}`;
  window.clearTimeout(eventTimer);
  eventTimer = window.setTimeout(() => {
    actionLabel.textContent = "继续做个动作吧";
    statusDetail.textContent = "肩部以上入镜也能互动";
  }, event.name === "USER_LEFT" ? 2_500 : 3_000);
  if (event.name === "USER_ENTERED") burstUntil = performance.now() + 3_000;
}

function showCalibrationPrompt(): void {
  if (!running || viewpointController.isCalibrated || viewpointController.isCalibrating || !scene3d.isEnabled) return;
  calibrationTitle.textContent = "正对屏幕，校准3D";
  calibrationCopy.textContent = "保持头部自然居中，然后点击按钮";
  calibrationButton.textContent = "校准3D";
  calibrationButton.disabled = false;
  calibrationPanel.hidden = false;
}

function beginCalibration(): void {
  viewpointController.beginCalibration(performance.now());
  sessionStorage.removeItem(CALIBRATION_KEY);
  calibrationPanel.hidden = false;
  calibrationTitle.textContent = "正在校准";
  calibrationCopy.textContent = "请保持正对屏幕约半秒";
  calibrationButton.textContent = "采集中…";
  calibrationButton.disabled = true;
  burstUntil = performance.now() + 1_000;
}

function finishCalibration(): void {
  const calibration = viewpointController.savedCalibration;
  if (calibration) sessionStorage.setItem(CALIBRATION_KEY, JSON.stringify(calibration));
  calibrationTitle.textContent = "3D校准完成";
  calibrationCopy.textContent = "现在左右移动头部看看";
  calibrationButton.textContent = "完成";
  window.setTimeout(() => { calibrationPanel.hidden = true; }, 1_200);
}

function drawDebug(landmarks: Landmark[] | null): void {
  const context = debugCanvas.getContext("2d");
  if (!context) return;
  const width = cameraVideo.videoWidth || 640;
  const height = cameraVideo.videoHeight || 480;
  if (debugCanvas.width !== width || debugCanvas.height !== height) {
    debugCanvas.width = width;
    debugCanvas.height = height;
  }
  context.clearRect(0, 0, width, height);
  if (!debugEnabled || !landmarks) return;
  context.fillStyle = "#8affd1";
  context.strokeStyle = "rgba(138, 255, 209, .75)";
  context.lineWidth = 3;
  const connections = [[11, 12], [11, 13], [13, 15], [12, 14], [14, 16], [11, 23], [12, 24], [23, 24]];
  for (const [from, to] of connections) {
    const a = landmarks[from];
    const b = landmarks[to];
    if (!a || !b || (a.visibility ?? 1) < 0.35 || (b.visibility ?? 1) < 0.35) continue;
    context.beginPath();
    context.moveTo(a.x * width, a.y * height);
    context.lineTo(b.x * width, b.y * height);
    context.stroke();
  }
  for (const point of landmarks) {
    if ((point.visibility ?? 1) < 0.35) continue;
    context.beginPath();
    context.arc(point.x * width, point.y * height, 4, 0, Math.PI * 2);
    context.fill();
  }
}

worker.onmessage = (message: MessageEvent<WorkerResponse>) => {
  const data = message.data;
  if (data.type === "PROGRESS") {
    progress.hidden = false;
    progressBar.style.width = `${Math.round(data.progress * 100)}%`;
    startCopy.textContent = data.stage;
  } else if (data.type === "READY") {
    workerReady = true;
    progressBar.style.width = "100%";
    maybeRun();
  } else if (data.type === "RESULT") {
    framePending = false;
    posePresent = Boolean(data.landmarks);
    const bestEvent = [...data.events].sort((a, b) => EVENT_PRIORITY[b.name] - EVENT_PRIORITY[a.name])[0];
    if (bestEvent) showEvent(bestEvent);
    debugPose.textContent = `Pose：${data.metrics.poseMs.toFixed(0)}ms / ${data.metrics.inferenceFps.toFixed(1)}fps`;
    debugHand.textContent = `Hand：${data.metrics.handMs.toFixed(0)}ms / ${data.handLandmarks.length}只`;
    debugLatency.textContent = `推理：${(data.metrics.poseMs + data.metrics.handMs).toFixed(0)}ms`;
    const now = performance.now();
    const calibrationProgress = viewpointController.ingest(
      data.viewpoint,
      now,
      Math.max(0.2, window.innerWidth / window.innerHeight),
    );
    if (calibrationProgress === "progress") {
      calibrationCopy.textContent = `请保持正对屏幕（${viewpointController.sampleCount}/10）`;
    } else if (calibrationProgress === "complete") {
      finishCalibration();
    } else if (calibrationProgress === "failed") {
      calibrationTitle.textContent = "没有稳定识别到双眼";
      calibrationCopy.textContent = "请正对镜头、保持光线充足后重试";
      calibrationButton.textContent = "重新校准";
      calibrationButton.disabled = false;
    }
    if (data.viewpoint) {
      if (!viewpointVisibleSince) viewpointVisibleSince = now;
      if (now - viewpointVisibleSince >= 300) showCalibrationPrompt();
      debugViewpoint.textContent = `眼位：${lastEyePosition.x.toFixed(2)}, ${lastEyePosition.y.toFixed(2)}, ${lastEyePosition.z.toFixed(2)} / ${(data.viewpoint.confidence * 100).toFixed(0)}%`;
    } else {
      viewpointVisibleSince = 0;
      debugViewpoint.textContent = "眼位：未识别";
    }
    debugRender.textContent = `3D：${sceneEnabled && scene3d.isEnabled ? "开启" : "关闭"} / ${scene3d.fps.toFixed(0)}fps`;
    drawDebug(data.landmarks);
  } else if (data.type === "ERROR") {
    framePending = false;
    startButton.disabled = false;
    startError.textContent = `识别暂时不可用：${data.message}`;
    cameraState.textContent = "识别异常";
    debugEvent.textContent = `错误：${data.message}`;
  }
};

function cameraErrorMessage(error: unknown): string {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError") return "摄像头权限未开启，请在 Safari 设置中允许访问后重试。";
    if (error.name === "NotFoundError") return "没有找到可用摄像头。";
    if (error.name === "NotReadableError") return "摄像头正被其他应用占用，请关闭后重试。";
  }
  return error instanceof Error ? error.message : "启动失败，请刷新后重试。";
}

startButton.addEventListener("click", async () => {
  startButton.disabled = true;
  startError.textContent = "";
  progress.hidden = false;
  progressBar.style.width = "5%";
  startCopy.textContent = "正在准备机器人和摄像头…";
  try {
    if (!sceneEnabled || !scene3d.isEnabled) await player.unlock();
    post({ type: "INIT" });
    await startCamera();
  } catch (error) {
    startButton.disabled = false;
    startCopy.textContent = "摄像头画面只在当前手机内处理，不会上传或保存。";
    startError.textContent = cameraErrorMessage(error);
  }
});

debugButton.addEventListener("click", () => {
  debugEnabled = !debugEnabled;
  debugButton.setAttribute("aria-pressed", String(debugEnabled));
  debugButton.textContent = debugEnabled ? "关闭调试" : "调试";
  debugPanel.hidden = !debugEnabled;
  debugCanvas.hidden = !debugEnabled;
  if (!debugEnabled) drawDebug(null);
});

calibrationButton.addEventListener("click", beginCalibration);
debugRecalibrate.addEventListener("click", beginCalibration);
debug3dToggle.addEventListener("click", () => {
  sceneEnabled = !sceneEnabled;
  scene3d.setEnabled(sceneEnabled);
  app.classList.toggle("scene-disabled", !sceneEnabled);
  debug3dToggle.setAttribute("aria-pressed", String(sceneEnabled));
  debug3dToggle.textContent = sceneEnabled ? "关闭3D" : "开启3D";
  player.setActive(!sceneEnabled);
  if (!sceneEnabled) void player.unlock();
  if (!sceneEnabled) calibrationPanel.hidden = true;
});

function setGuideOpen(open: boolean): void {
  guidePanel.hidden = !open;
  guideButton.setAttribute("aria-expanded", String(open));
}

guideButton.addEventListener("click", () => setGuideOpen(true));
guideClose.addEventListener("click", () => setGuideOpen(false));
guidePanel.addEventListener("click", (event) => {
  if (event.target === guidePanel) setGuideOpen(false);
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") setGuideOpen(false);
});

document.addEventListener("visibilitychange", () => {
  if (!document.hidden && running) {
    framePending = false;
    post({ type: "RESET" });
  }
});

window.addEventListener("resize", () => scene3d.resize());
window.addEventListener("orientationchange", () => {
  sessionStorage.removeItem(CALIBRATION_KEY);
  viewpointController.reset();
  calibrationPanel.hidden = true;
  viewpointVisibleSince = 0;
  window.setTimeout(() => scene3d.resize(), 150);
});

window.addEventListener("beforeunload", () => {
  stream?.getTracks().forEach((track) => track.stop());
  worker.terminate();
  scene3d.dispose();
});
